import React, { useState, useEffect } from 'react';
import "bootstrap/dist/css/bootstrap.min.css";
import authService from './api-authorization/AuthorizeService'
import axios from 'axios'
import { ThreeDots } from "react-loader-spinner";
import MUIDataTable, { ExpandButton } from "mui-datatables";
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayerFlag from './PlayerFlag'
import PlayerGames from './PlayerGames'
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { Link } from "react-router-dom";

const delay = ms => new Promise(res => setTimeout(res, ms));

const muiCache = createCache({
    key: 'mui-datatables',
    prepend: true,
});

export default function FindPlayers(props) {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
const [searchString, setSearchString] = useState("");
const [exactMatch, setExactMatch] = useState(false);
const [searchStatus, setSearchStatus] = useState();
const [searchStatusColor, setSearchStatusColor] = useState();
const [players, setPlayers] = useState([]);
const [selectedPlayer, setSelectedPlayer] = useState();
const [searching, setSearching] = useState(false);
const [loading, setLoading] = useState(false);
const [includeGamesOfSameName, setIncludeGamesOfSameName] = useState(false);
const [excludeBlitzGames, setExcludeBlitzGames] = useState(false);
const [authenticated, setAuthenticated] = useState(false);
const [isAdmin, setIsAdmin] = useState(false);
const [userRole, setUserRole] = useState("");
const [selectPlayer, setSelectPlayer] = useState(props.selectPlayer ? props.selectPlayer : false);
const [expandedRows, setExpandedRows] = useState([]);

    useEffect(async () => {
        setAuthenticated(await authService.isAuthenticated());
        var user = await authService.getUser();
        if (user != null) {
            var role = user.role;
            setUserRole(role);
            if (role.includes("Administrators"))
                setIsAdmin(true);
        }
    }, []);

    // Mobile-friendly expandable row content
    const renderExpandedRow = (rowData, rowMeta) => {
        const player = players[rowMeta.dataIndex];
        if (!player) return null;
        
        const idType = player.fidePlayerId > 0 ? "FIDE" : "USCF";
        const id = player.fidePlayerId > 0 ? player.fidePlayerId : player.uscfPlayerId;
        
        return (
            <TableRow>
                <TableCell colSpan={6} sx={{ py: 2, px: 2, backgroundColor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Country</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PlayerFlag code={player.fideCountry || "USA"} />
                                    <Typography variant="body2">{player.fideCountry || "USA"}</Typography>
                                </Box>
                            </Box>
                            {player.uscfState && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">State</Typography>
                                    <Typography variant="body2">{player.uscfState}</Typography>
                                </Box>
                            )}
                            {player.fideBirthday > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Birth Year</Typography>
                                    <Typography variant="body2">{player.fideBirthday}</Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {player.fidePlayerId > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">FIDE ID</Typography>
                                    <Typography variant="body2">
                                        <a href={`https://ratings.fide.com/profile/${player.fidePlayerId}`} target="_blank" rel="noreferrer">
                                            {player.fidePlayerId}
                                        </a>
                                    </Typography>
                                </Box>
                            )}
                            {player.uscfPlayerId > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">USCF ID</Typography>
                                    <Typography variant="body2">
                                        <a href={`https://ratings.uschess.org/player/${player.uscfPlayerId}`} target="_blank" rel="noreferrer">
                                            {player.uscfPlayerId}
                                        </a>
                                    </Typography>
                                </Box>
                            )}
                            {player.uscfRating > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">USCF Rating</Typography>
                                    <Typography variant="body2">{player.uscfRating}</Typography>
                                </Box>
                            )}
                            {player.fideBlitzRating > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">FIDE Blitz</Typography>
                                    <Typography variant="body2">{player.fideBlitzRating}</Typography>
                                </Box>
                            )}
                            {player.fideRapidRating > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">FIDE Rapid</Typography>
                                    <Typography variant="body2">{player.fideRapidRating}</Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Button 
                                component={Link} 
                                target="_blank" 
                                to={`/players/details?idtype=${idType}&id=${id}`} 
                                variant="contained" 
                                color="primary"
                                size="small"
                                fullWidth
                            >
                                View Games
                            </Button>
                        </Box>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    // Define columns based on screen size
    const getColumns = () => {
        if (isMobile) {
            // Mobile: Show minimal columns with expand for more details
            // Columns: # | Name (with title, country, state) | Rating | Games button
            return [
                {
                    name: '',
                    label: '#',
                    options: {
                        filter: false,
                        sort: false,
                        setCellProps: () => ({ style: { width: '28px', padding: '4px 2px' } }),
                        customBodyRender: (value, tableMeta) => {
                            return <span style={{ fontSize: '11px', color: '#666' }}>{tableMeta.rowIndex + 1}</span>;
                        }
                    }
                },
                {
                    name: "fideName",
                    label: "Player",
                    options: {
                        filter: false,
                        sort: true,
                        setCellProps: () => ({ style: { padding: '4px' } }),
                        customBodyRender: (value, tableMeta) => {
                            let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                            let rowPlayer = players[originalIndex];
                            let displayName = value || (rowPlayer ? rowPlayer.uscfName : '');
                            let title = rowPlayer?.fideTitle || '';
                            let country = rowPlayer?.fideCountry || '';
                            let state = rowPlayer?.uscfState || '';
                            
                            // Build location string
                            let locationParts = [];
                            if (country) locationParts.push(country);
                            if (state) locationParts.push(state);
                            let locationStr = locationParts.join('/');
                            
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.2 }}>
                                        {title && <span style={{ color: '#1976d2', fontWeight: 'bold' }}>{title} </span>}
                                        {displayName}
                                    </Typography>
                                    {locationStr && (
                                        <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1 }}>
                                            {locationStr}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }
                    }
                },
                {
                    name: "fideRating",
                    label: "Rating",
                    options: {
                        filter: false,
                        sort: true,
                        sortDirection: 'desc',
                        setCellProps: () => ({ style: { width: '60px', padding: '4px', textAlign: 'center' } }),
                        customBodyRender: (value, tableMeta) => {
                            let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                            let rowPlayer = players[originalIndex];
                            let fideRating = rowPlayer?.fideRating || 0;
                            let uscfRating = rowPlayer?.uscfRating || 0;
                            
                            // Use FIDE rating if available, otherwise use USCF rating with (US) suffix
                            if (fideRating > 0) {
                                return <span style={{ fontSize: '12px', fontWeight: 500 }}>{fideRating}</span>;
                            } else if (uscfRating > 0) {
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 500 }}>{uscfRating}</span>
                                        <span style={{ fontSize: '9px', color: '#666' }}>(US)</span>
                                    </Box>
                                );
                            } else {
                                return <span style={{ fontSize: '12px', color: '#999' }}>-</span>;
                            }
                        }
                    }
                },
                {
                    name: "",
                    label: "",
                    options: {
                        filter: false,
                        sort: false,
                        setCellProps: () => ({ style: { width: '60px', padding: '4px 2px' } }),
                        customBodyRender: (value, tableMeta) => {
                            let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                            let rowPlayer = players[originalIndex];
                            let idType = rowPlayer.fidePlayerId > 0 ? "FIDE" : "USCF";
                            let id = rowPlayer.fidePlayerId > 0 ? rowPlayer.fidePlayerId : rowPlayer.uscfPlayerId;
                            return (
                                <Button 
                                    component={Link} 
                                    target="_blank" 
                                    to={`/players/details?idtype=${idType}&id=${id}`} 
                                    variant="contained" 
                                    color="primary"
                                    size="small"
                                    sx={{ fontSize: '10px', py: 0.5, px: 1, minWidth: 'auto' }}
                                >
                                    Games
                                </Button>
                            );
                        }
                    }
                }
            ];
        }
        
        // Desktop: Full columns
        return [
        {
            name: '',
            label: '',
            options: {
                filter: false,
                customBodyRender: (value, tableMeta, update) => {
                    let rowIndex = Number(tableMeta.rowIndex) + 1;
                    return (
                        <div style={{ textAlign: "right" }}><span>{rowIndex}</span></div>
                    )
                }
            }
        },
        {
            name: "fideTitle",
            label: "Title",
            options: {
                filter: true,
                filterType: 'multiselect',
                sort: true,
                sortThirdClickReset: true,
            }
        },
        {
            name: "fideWomenTitle",
            label: "Women Title",
            options: {
                filter: true,
                filterType: 'multiselect',
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "fideBirthday",
            label: "Birthday Year",
            options: {
                filter: true,
                filterType: 'multiselect',
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "fideCountry",
            label: "Flag",
            options: {
                filter: true,
                filterType: 'multiselect',
                sort: true,
                sortOrder: { name: 'id', direction: 'desc' },
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => (
                    <div>
                        <PlayerFlag key={value} code={(value === "" || value === null || value === undefined) ? "USA" : value} />
                    </div>
                )
            }
        },
        {
            name: "fideCountry",
            label: "Country",
            options: {
                filter: true,
                filterType: 'multiselect',
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => (
                    <div>
                        <span>{value ? value : "USA"}</span>
                    </div >

                )
            }
        },
        {
            name: "uscfState",
            label: "State",
            options: {
                filter: true,
                filterType: 'multiselect',
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => (
                    <span>
                        {value === "" ? "--" : value}
                    </span>
                )
            }
        },
        {
            name: "fideName",
            label: "Name",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                    let rowPlayer = players[originalIndex];
                    let idType = rowPlayer.fidePlayerId > 0 ? "FIDE" : "USCF";
                    let id = rowPlayer.fidePlayerId > 0 ? rowPlayer.fidePlayerId : rowPlayer.uscfPlayerId;
                    return (
                            <Link target="_blank" to={'/players/details?idtype='+idType+'&id='+id} className="button">
                                {value ? value : rowPlayer.uscfName}
                            </Link>
                    )
                }
            }
        },
        {
            name: "fidePlayerId",
            label: "FIDE ID",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                    let rowPlayer = players[originalIndex];
                    let birthYear = rowPlayer.fideBirthday;
                    return (
                        <div style={{ textAlign: "left" }}> {value != -1 ? (<a href={'https://ratings.fide.com/profile/' + value} target='_blank' rel='noreferrer'>{value} [{birthYear}]</a>) : (value)}</div>
                    );
                }
            }
        },
        {
            name: "fideRating",
            label: "Elo",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                    let rowPlayer = players[originalIndex];
                    let fideId = rowPlayer.fidePlayerId;
                    return (
                        <div style={{ textAlign: "left" }}> {fideId != -1 ? (<a href={'https://ratings.fide.com/profile/' + fideId + "/calculations"} target='_blank' rel='noreferrer'>{value}</a>) : (value)}</div>
                    );
                }
            }
        },
        {
            name: "fideBlitzRating",
            label: "FIDE Blitz",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "fideRapidRating",
            label: "FIDE Rapid",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "uscfPlayerId",
            label: "USCF ID",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    return (
                        <div style={{ textAlign: "left" }}> {value != -1 ? (<a href={'https://ratings.uschess.org/player/' + value} target='_blank' rel='noreferrer'>{value}</a>) : (value)}</div>
                    );
                }
            }
        },
        {
            name: "uscfRating",
            label: "Rating",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,

                customBodyRender: (value, tableMeta, updateValue) => {
                    let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                    let rowPlayer = players[originalIndex];
                    let uscfId = rowPlayer.uscfPlayerId;
                    return (
                        <div style={{ textAlign: "left" }}> {uscfId != -1 ? (<a href={'https://ratings.uschess.org/player/' + uscfId} target='_blank' rel='noreferrer'>{value}</a>) : (value)}</div>
                    );
                }
            }
        },
        {
            name: "uscfOnlineRating",
            label: "USCF Online",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "uscfBlitzRating",
            label: "USCF Blitz",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "uscfOnlineBlitzRating",
            label: "USCF Online Blitz",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "uscfQuickRating",
            label: "USCF Quick",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "uscfOnlineQuickRating",
            label: "USCF Online Quick",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                display: false,
                viewColumns: true
            }
        },
        {
            name: "",
            label: "",
            options: {
                filter: false,
                sort: true,
                sortThirdClickReset: true,
                customBodyRender: (value, tableMeta, updateValue) => {
                    let originalIndex = tableMeta.currentTableData[tableMeta.rowIndex].index;
                    let rowPlayer = players[originalIndex];
                    let idType = rowPlayer.fidePlayerId > 0 ? "FIDE" : "USCF";
                    let id = rowPlayer.fidePlayerId > 0 ? rowPlayer.fidePlayerId : rowPlayer.uscfPlayerId;
                    return (
                        <Button component={Link} target="_blank" to={'/players/details?idtype=' + idType + '&id=' + id} variant="contained" color="primary">
                                Games
                        </Button>
                    );
                }
            }
        }
    ];
    };

    const columns = getColumns();

    const options = {
        setTableProps: () => {
            return {
                size: 'small',
            };
        },
        filter: !isMobile,
        filterType: 'dropdown',
        responsive: isMobile ? "standard" : "vertical",
        expandableRows: isMobile,
        expandableRowsOnClick: isMobile,
        expandableRowsHeader: false,
        renderExpandableRow: isMobile ? renderExpandedRow : undefined,
        selectableRows: 'none',
        download: false,
        print: false,
        viewColumns: !isMobile,
        search: !isMobile,
        rowsPerPage: 10,
        rowsPerPageOptions: [10, 20, 50],
        fixedHeaderOptions: {
            xAxis: true,
            yAxis: true
        },
        resizableColumns: false,
        isRowExpandable: (dataIndex, expandedRows) => {
            if (expandedRows.data.length > 4 && expandedRows.data.filter(d => d.dataIndex === dataIndex).length === 0)
                return false;
            return true;
        },
        rowsExpanded: expandedRows,
        onRowExpansionChange: (curExpanded, allExpanded, rowsExpanded) => {
            setExpandedRows(allExpanded.map(r => r.dataIndex));
        },
        onRowClick: (rowData, rowMeta) => {
            const detail = players[rowMeta.dataIndex];
            console.log(detail);
            if (props.sendToParentSelectedPlayer) {
                props.sendToParentSelectedPlayer(detail)
            }
            if (detail.fidePlayerId > 0) {
                if (props.sendToParentIdType) {
                    props.sendToParentIdType("FIDE")
                }
                if (props.sendToParentPlayerId) {
                    props.sendToParentPlayerId(detail.fidePlayerId)
                }
            } else {
                if (props.sendToParentIdType) {
                    props.sendToParentIdType("USCF")
                }
                if (props.sendToParentPlayerId) {
                    props.sendToParentPlayerId(detail.uscfPlayerId)
                }
            }
        },
        textLabels: {
            body: {
                noMatch: "No players found",
            },
            pagination: {
                rowsPerPage: isMobile ? "Rows:" : "Rows per page:",
            },
        },
    };

    const tableTheme = createTheme({
        overrides: {
            MUIDataTableSelectCell: {
                expandDisabled: {
                    // Soft hide the button.
                    visibility: 'hidden',
                },
            },
        },
        components: {
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        padding: isMobile ? '6px 4px' : '16px',
                    },
                },
            },
        },
    });

    const components = {
        ExpandButton: function (props) {
            return <ExpandButton {...props} />;
        },
    };



    const findPlayers = async (e, searchString) => {
        if (searchString === "") {
            e.preventDefault();
            setSearchStatus("Please enter player names or ID");
            setSearchStatusColor("red");
            setLoading(false);
        }
        else {
            e.preventDefault();
            setSearching(true);
            var token = await authService.getAccessToken();
            var headers = {
                headers: { 'Authorization': `Bearer ${token}` }
            }
            axios.get('/api/ChessPlayers/FindPlayer?searchString=' + searchString + '&exactMatch=' + exactMatch, headers).then((response) => {
                const serachResults = response.data;
                setPlayers(serachResults);
                setSearchStatus(serachResults.length + ' players found by searching "' + searchString + '"');
                setSearchStatusColor("green");
                setSearching(false);
                setLoading(false);
            }).catch((error) => {
                setSearchStatusColor("red");
                setPlayers([]);
                setSearchStatus(error.response.data);
                setSearching(false);
                setLoading(false);
            });
        }
    };


    const handleKeyDown = e => {
        const trimmedText = e.target.value.trim()
        // If the user pressed the Enter key:
        if (e.key === 'Enter' && trimmedText) {
            setLoading(true);
            findPlayers(e, searchString);
            setSelectedPlayer(null);
        }
    }

    return (
        <Box sx={{ pt: 2, pb: 1, px: isMobile ? 1 : 0 }}>
            <Grid container direction="row" rowSpacing={1} columnSpacing={1} justifyContent="space-between" alignItems="stretch">
                <Grid item xs={12} sm={7} lg={6}>
                        <TextField
                            size="small"
                            fullWidth
                            id="searchString"
                            label={isMobile ? "Player ID or Name" : "Enter player's ID or Name (e.g. 1234567 | Santa Clause )"}
                            InputLabelProps={{
                                shrink: true
                            }}
                            value={searchString}
                        onChange={async (e) => { await setSearchString(e.target.value) }}
                        onKeyDown={handleKeyDown}
                        />
                </Grid>
                <Grid item xs={6} sm={5} lg={2}>
                    <FormControlLabel 
                        control={<Checkbox checked={exactMatch} onChange={e => { setExactMatch(e.target.checked); }} size={isMobile ? "small" : "medium"} />} 
                        label={<Typography variant={isMobile ? "body2" : "body1"}>Exact Match</Typography>}
                    />
                </Grid>
                <Grid item xs={6} sm={12} lg={4}>
                    <LoadingButton 
                        sx={{ textTransform: "none" }} 
                        endIcon={!isMobile && <PersonSearchIcon />} 
                        fullWidth 
                        size={isMobile ? "medium" : "large"}
                        onClick={async (e) => { await setLoading(true); await findPlayers(e, searchString); setSelectedPlayer(null); setExpandedRows([]); }} 
                        loading={loading} 
                        loadingIndicator="Loading..." 
                        variant="contained"
                    >
                        {isMobile ? "Search" : (props.searchButtonCaption ? props.searchButtonCaption : "Search Players & Download Games")}
                    </LoadingButton>
                </Grid>
                {/*<Grid item xs={12} lg={1}>*/}
                {/*    */}{/*<Stack spacing={1} direction="row" justifyContent="end">*/}
                {/*    */}{/*<Button endIcon={<ClearAllIcon/>} fullWidth variant="outlined" onClick={async (e) => { e.preventDefault(); await setSearchString(""); await setSearchStatus(""); await setPlayers([]); await setSelectedPlayer() }}>{props.clearButtonCaption ? props.clearButtonCaption : "Clear"}</Button>*/}
                {/*    */}{/*</Stack>*/}
                {/*    */}{/*<Link to="/Contribute" className="btn btn-primary"><Button variant="contained" color="secondary">Contribute</Button></Link>*/}
                {/*    <Link fullWidth to="/Contribute" className="btn btn-success fullWidth">Contribute</Link>*/}
                {/*</Grid>*/}
                {/*<Grid item xs={12} lg={1}>*/}
                {/*    <Button sx={{ textTransform: "none" }} fullWidth variant="contained" color="secondary" href="/Contribute">Contribute</Button>*/}
                {/*</Grid>*/}
                {searching &&
                    <Grid item xs={12}>
                        <div style={{ zIndex: 99, position: 'fixed', top: '20%', left: '50%' }}>
                            <span> <ThreeDots ariaLabel="Searching " color="#00BFFF" /></span>
                        </div>
                    </Grid>
                }
                {players.length === 0 &&
                    <Grid item xs={12}>
                        <div>
                            <span className="input-group-addon mt-1 mb-1 ml-1" style={{ color: searchStatusColor }}>{searchStatus}</span>
                        </div>
                    </Grid>
                }
                {selectedPlayer &&
                    <Grid item xs={12}>
                    <PlayerGames key={selectedPlayer.uscfPlayerId + selectedPlayer.fidePlayerId} player={selectedPlayer} includeGamesOfSameName={includeGamesOfSameName} excludeBlitzGames={excludeBlitzGames}/>
                    </Grid>
                }
                {/*{players.length > 0 &&*/}
                {/*    <Grid item xs={12}>*/}
                {/*        <div className="mt-10 mb-10">*/}
                {/*            <FormGroup>*/}
                {/*                <FormControlLabel control={<Checkbox disabled={!authenticated}*/}
                {/*                    onChange={e => {*/}
                {/*                        setExcludeBlitzGames(e.target.checked);*/}
                {/*                    }}*/}
                {/*            />} label="Exclude Blitz Games [Log in required]" />*/}
                {/*            </FormGroup>*/}
                {/*        </div>*/}
                {/*    </Grid>*/}
                {/*}*/}
                {/*{players.length > 0 &&*/}
                {/*    <Grid item xs={12}>*/}
                {/*        <div className="mt-10 mb-10">*/}
                {/*            <FormGroup>*/}
                {/*                <FormControlLabel control={<Checkbox disabled={!authenticated}*/}
                {/*                    onChange={e => {*/}
                {/*                        setIncludeGamesOfSameName(e.target.checked);*/}
                {/*                    }}*/}
                {/*                />} label="Include untagged games with similar names [Log in required]" />*/}
                {/*            </FormGroup>*/}
                {/*        </div>*/}
                {/*    </Grid>*/}
                {/*}*/}
                {players.length > 0 &&
                    <Grid item xs={12}>
                    <div className="mt-10 mb-10">
                        <CacheProvider value={muiCache}>
                            <ThemeProvider theme={tableTheme}>
                                <MUIDataTable
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                            <Button variant="contained" color="secondary" size={isMobile ? "small" : "medium"}
                                                onClick={async (e) => { e.preventDefault(); await setSearchString(""); await setSearchStatus(""); await setPlayers([]); await setSelectedPlayer(); setExpandedRows([]); }}>
                                                {"Clear"}
                                            </Button>
                                            <Typography variant={isMobile ? "body2" : "body1"} component="span">
                                                {players.length + " player(s) found"}
                                                {!isMobile && ' by searching "' + searchString + '"'}
                                            </Typography>
                                            {isMobile && (
                                                <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                                    Tap a row to expand details
                                                </Typography>
                                            )}
                                        </div>
                                    }
                                    data={players}
                                    columns={columns}
                                    options={options}
                                    components={components}
                                />
                            </ThemeProvider>
                        </CacheProvider>
                    </div>
                    </Grid>
                }
                {/*{players.length > 0 &&*/}
                {/*    <Grid item xs={12}>*/}
                {/*        <div className="mt-10 mb-10">*/}
                {/*            <FormGroup>*/}
                {/*                <FormControlLabel control={<Checkbox disabled={!authenticated}*/}
                {/*                    onChange={e => {*/}
                {/*                        setIncludeGamesOfSameName(e.target.checked);*/}
                {/*                    }}*/}
                {/*            />} label="Include untagged games with similar names [Log in required]" />*/}
                {/*            </FormGroup>*/}
                {/*        </div>*/}
                {/*    </Grid>*/}
                {/*}*/}
                {/*{selectedPlayer && */}
                {/*    <Grid item xs={12}>*/}
                {/*    <PlayerGames key={selectedPlayer.uscfPlayerId+selectedPlayer.fidePlayerId} player={selectedPlayer}/>*/}
                {/*    </Grid>*/}
                {/*}*/}
                
            </Grid>
        </Box>
        )
}

